import * as React from "react";

import { cn } from "@/lib/utils";
import { FormFieldContext } from "@/components/ui/form-field";

// Accessible, token-styled searchable single-select combobox (Story 6.3, level-1
// diagnosis group). A select-only combobox button (role="combobox") that, when open,
// reveals an inline panel with a filter textbox + a listbox of options. Rendered inline
// (no portal) so it stays robustly testable with the house fireEvent style — no
// scrollIntoView/pointer-capture polyfills, no user-event dependency.
//
// A11y (WCAG 2.1 AA): the trigger is labelable (label[htmlFor] → button), carries
// aria-expanded / aria-haspopup="listbox" / aria-controls; the search box drives
// aria-activedescendant over the listbox; options expose role="option" + aria-selected;
// ArrowUp/Down/Home/End move the active option, Enter selects, Escape closes and
// returns focus to the trigger. Tokens only — no hardcoded hex.

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * A real candidate that cannot be chosen right now (Story 22.1 AC22): it stays
   * in the list, carries its reason in the label, and refuses selection. HIDING
   * it is the thing this option exists to prevent — hidden, the reader cannot
   * tell "wrong search" from "this one is unavailable". `aria-disabled` rather
   * than `disabled`, so it is still reachable and announced: an option a screen
   * reader cannot land on is hidden by another name.
   */
  disabled?: boolean;
}

export interface SearchableComboboxProps {
  /** Id for the trigger. Optional when placed inside a <FormField> — auto-derived from context. */
  id?: string;
  options: ComboboxOption[];
  /** Selected option value, or "" when nothing is selected. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Accessible name for the inline search box, e.g. "Search diagnosis groups". */
  searchAriaLabel: string;
  /** Accessible name for the clear button, e.g. "Clear diagnosis group". */
  clearAriaLabel?: string;
  disabled?: boolean;
  /**
   * When false, the search input is hidden and keyboard navigation runs from
   * the trigger — suitable for short option lists (e.g. 3–5 items).
   * Defaults to true.
   */
  showSearch?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const triggerClass =
  "flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-[14px] py-[10px] text-left text-md text-[color:var(--text-primary)] transition-colors focus:border-[color:var(--border-brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-[color:var(--border-error)] aria-[invalid=true]:focus:ring-[color:var(--focus-ring-error)]";

export const SearchableCombobox = React.forwardRef<HTMLButtonElement, SearchableComboboxProps>(
  function SearchableCombobox(
  {
  id,
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Type to search…",
  emptyText = "No matches found",
  searchAriaLabel,
  clearAriaLabel,
  disabled = false,
  showSearch = true,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: SearchableComboboxProps,
  forwardedRef,
) {
  const generatedId = React.useId();
  const ctx = React.useContext(FormFieldContext);
  const resolvedId = id ?? ctx?.id ?? generatedId;
  const resolvedInvalid = ariaInvalid ?? (ctx?.hasError ? true : undefined);
  const resolvedDescribedBy =
    [ariaDescribedBy, ctx?.describedBy].filter(Boolean).join(" ") || undefined;

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  React.useImperativeHandle(forwardedRef, () => triggerRef.current!);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const listboxId = `${resolvedId}-listbox`;

  const selectedOption = options.find((o) => o.value === value);
  // Fall back to the raw value so a stored-but-unresolved label still shows its text.
  const displayText = value ? selectedOption?.label ?? value : "";

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Reset the active option whenever the visible set changes (new query results or
  // updated options) so the highlight never points at a stale row — e.g. when an
  // option above the active one is filtered out, or the options prop changes while open.
  React.useEffect(() => {
    setActiveIndex(0);
  }, [filtered]);

  // Focus the search box when the panel opens (only in search mode).
  React.useEffect(() => {
    if (open && showSearch) searchRef.current?.focus();
  }, [open, showSearch]);

  // Close on outside pointer-down (no blur juggling, so option clicks land cleanly).
  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
  }, [open]);

  function openPanel() {
    if (disabled) return;
    setQuery("");
    const selectedIdx = options.findIndex((o) => o.value === value);
    setActiveIndex(!showSearch && selectedIdx >= 0 ? selectedIdx : 0);
    setOpen(true);
  }

  function closePanel(returnFocus = true) {
    setOpen(false);
    setQuery("");
    if (returnFocus) triggerRef.current?.focus();
  }

  function select(option: ComboboxOption) {
    // One refusal point, reached by BOTH the click handler and the Enter key.
    // Two guards could drift, and the one that drifted would be the keyboard.
    if (option.disabled) return;
    onChange(option.value);
    closePanel();
  }

  function clear() {
    onChange("");
    // Self-close if the panel is open so clearing never leaves an orphaned open panel
    // with focus jumped to the trigger; closePanel also resets the search query.
    closePanel();
  }

  function onTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPanel();
      }
      return;
    }
    // Panel open, no search box — navigate directly from the trigger.
    if (!showSearch) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setActiveIndex((i) => (options.length ? (i + 1) % options.length : 0));
          break;
        case "ArrowUp":
          event.preventDefault();
          setActiveIndex((i) =>
            options.length ? (i - 1 + options.length) % options.length : 0,
          );
          break;
        case "Home":
          event.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          event.preventDefault();
          setActiveIndex(Math.max(0, options.length - 1));
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (options[activeIndex]) select(options[activeIndex]);
          break;
        case "Escape":
          event.preventDefault();
          closePanel();
          break;
        case "Tab":
          closePanel();
          break;
      }
    }
  }

  function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => (filtered.length ? (i + 1) % filtered.length : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) =>
          filtered.length ? (i - 1 + filtered.length) % filtered.length : 0,
        );
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(Math.max(0, filtered.length - 1));
        break;
      case "Enter":
        event.preventDefault();
        if (filtered[activeIndex]) select(filtered[activeIndex]);
        break;
      case "Escape":
        event.preventDefault();
        closePanel();
        break;
      default:
        break;
    }
  }

  const activeOptionId =
    open && filtered.length ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        id={resolvedId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={!showSearch ? activeOptionId : undefined}
        aria-invalid={resolvedInvalid}
        aria-describedby={resolvedDescribedBy}
        disabled={disabled}
        className={cn(triggerClass, "pr-[60px]")}
        onClick={() => (open ? closePanel(false) : openPanel())}
        onKeyDown={onTriggerKeyDown}
      >
        <span
          className={cn(
            "flex-1 min-w-0 truncate",
            !displayText && "text-[color:var(--text-placeholder)]",
          )}
        >
          {displayText || placeholder}
        </span>
      </button>
      {/* Controls overlay — sibling of the trigger so the clear control is a real
          <button> (no nested-button / role="button" hacks). The wrapper is
          pointer-events-none and the chevron passes clicks through to the trigger;
          only the clear button captures pointer events. */}
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
        {value && !disabled ? (
          <button
            type="button"
            aria-label={clearAriaLabel ?? "Clear selection"}
            className="pointer-events-auto rounded-sm px-1 text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]"
            onClick={(event) => {
              event.stopPropagation();
              clear();
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
        <span aria-hidden="true" className="text-[color:var(--text-tertiary)]">
          ▾
        </span>
      </div>

      {open ? (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-1 shadow-lg">
          {showSearch ? (
            <input
              ref={searchRef}
              type="text"
              // aria-autocomplete="list": this textbox filters the associated listbox, so
              // NVDA/JAWS announce that typing narrows the suggestions (WAI-ARIA combobox).
              aria-autocomplete="list"
              aria-label={searchAriaLabel}
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              autoComplete="off"
              placeholder={searchPlaceholder}
              value={query}
              // activeIndex resets via the `[filtered]` effect above (a query change produces
              // a new filtered set), so no redundant inline reset is needed here.
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              className="mb-1 flex min-h-[40px] w-full rounded-sm border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-[12px] py-[8px] text-md text-[color:var(--text-primary)] placeholder:text-[color:var(--text-placeholder)] focus:border-[color:var(--border-brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]"
            />
          ) : null}
          <div
            id={listboxId}
            role="listbox"
            aria-label={searchAriaLabel}
            className="max-h-[240px] overflow-auto"
          >
            {filtered.length === 0 ? (
              // Announce "no matches" as a status message, not a fake (disabled) option —
              // it is never a real selectable listbox entry.
              <div
                role="status"
                className="px-[12px] py-[8px] text-sm text-[color:var(--text-tertiary)]"
              >
                {emptyText}
              </div>
            ) : (
              filtered.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  // Option keyboard activation is driven from the search box
                  // (ArrowUp/Down + Enter via aria-activedescendant), the WAI-ARIA
                  // listbox pattern; a <button> keeps mouse-click handling interactive
                  // and keyboard-native while tabIndex={-1} keeps it out of the tab order.
                  <button
                    type="button"
                    key={option.value}
                    id={`${listboxId}-opt-${index}`}
                    role="option"
                    tabIndex={-1}
                    aria-selected={isSelected}
                    aria-disabled={option.disabled ? true : undefined}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => select(option)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-sm px-[12px] py-[8px] text-left text-md text-[color:var(--text-primary)]",
                      option.disabled
                        ? "cursor-not-allowed text-[color:var(--text-tertiary)]"
                        : "cursor-pointer",
                      isActive && "bg-[color:var(--bg-tertiary)]",
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected ? (
                      <span
                        aria-hidden="true"
                        className="text-[color:var(--text-brand-secondary)]"
                      >
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});
SearchableCombobox.displayName = "SearchableCombobox";
